import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import type { Locale } from '@/i18n';

interface SharedProps {
    locale: string;
    locales: string[];
    localeDomains: Record<string, string>;
    appDomain: string;
}

export function useLocale() {
    const { locale: i18nLocale } = useI18n();
    const page = usePage<SharedProps>();

    const locale = computed<Locale>(() => (page.props.locale as Locale) || (i18nLocale.value as Locale));
    const locales = computed(() => (page.props.locales as string[]) || []);
    const localeDomains = computed(() => (page.props.localeDomains as Record<string, string>) || {});

    /**
     * Build a full URL for a specific locale, pointing to the correct domain.
     */
    function urlForLocale(path: string, targetLocale: string): string {
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        const domain = localeDomains.value[targetLocale];

        if (domain) {
            return `${domain}${cleanPath}`;
        }

        return cleanPath;
    }

    /**
     * The app subdomain (app.novantt.com) for auth and dashboard.
     */
    const appDomain = computed(() => (page.props.appDomain as string) || '');

    /**
     * Build an absolute URL on the app subdomain for auth/dashboard routes.
     * Returns a relative path when already on the app domain.
     */
    function appUrl(path: string | { url: string }): string {
        const raw = typeof path === 'string' ? path : path.url;
        const cleanPath = raw.startsWith('/') ? raw : `/${raw}`;
        const domain = appDomain.value;

        if (!domain || typeof window === 'undefined') {
            return cleanPath;
        }

        // Already on the app domain — keep it relative for SPA navigation
        if (window.location.origin === domain) {
            return cleanPath;
        }

        return `${domain}${cleanPath}`;
    }

    /**
     * Whether we're currently on the app subdomain.
     */
    const isAppDomain = computed(() => {
        if (typeof window === 'undefined') return true;
        const domain = appDomain.value;
        return !domain || window.location.origin === domain;
    });

    /**
     * Get the current path (without domain or query string).
     */
    function currentPath(): string {
        return (page.url || '/').split('?')[0] || '/';
    }

    return {
        locale,
        locales,
        localeDomains,
        appDomain,
        isAppDomain,
        urlForLocale,
        appUrl,
        currentPath,
    };
}
